// CookiesToast.tsx
import { useEffect, useState } from 'react'
import toast, { Toast } from 'react-hot-toast'

// ----- Types & storage helpers
type ConsentStatus = 'custom'
type ConsentRecord = {
  status: ConsentStatus
  categories: {
    essential: true
    analytics: boolean
    marketing: boolean
  }
  ts: number
  source: 'system' | 'user'
}

const STORAGE_KEY = 'cookieConsent'

function getConsent(): ConsentRecord | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as ConsentRecord) : null
  } catch {
    return null
  }
}

function setConsent(record: ConsentRecord) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(record))
  window.dispatchEvent(new CustomEvent('cookie-consent', { detail: record }))
}

// Build a consent object with at least essential=true
function buildRecord(opts: Partial<ConsentRecord['categories']>, source: 'system' | 'user'): ConsentRecord {
  return {
    status: 'custom',
    categories: {
      essential: true,
      analytics: !!opts.analytics,
      marketing: !!opts.marketing,
    },
    ts: Date.now(),
    source,
  }
}

// ----- Public component
export function CookiesToast() {
  useEffect(() => {
    const existing = getConsent()

    if (!existing) {
      // First visit: opt-in default (all categories true), but mark as "system"
      setConsent(buildRecord({ analytics: true, marketing: true }, 'system'))
      toast.custom((t) => <ToastContent toast={t} />, {
        id: 'cookie-banner',
        duration: Infinity,
        position: 'bottom-center',
      })
      return
    }

    if (existing.source === 'system') {
      toast.custom((t) => <ToastContent toast={t} />, {
        id: 'cookie-banner',
        duration: Infinity,
        position: 'bottom-center',
      })
    }
  }, [])

  return null
}

// ----- Toast UI
function ToastContent({ toast: t }: { toast: Toast }) {
  const [showDetails, setShowDetails] = useState(false)

  const apply = (categories: ConsentRecord['categories']) => {
    setConsent(buildRecord(categories, 'user'))
    toast.dismiss(t.id)
  }

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-title"
      aria-describedby="cookie-desc"
      className="max-w-[720px] w-[calc(100vw-1rem)] rounded-2xl border border-gray-200 bg-white shadow-lg p-4 md:p-5"
    >
      {!showDetails ? (
        <div className="flex flex-col gap-4 md:flex-row md:items-center">
          <div className="flex-1">
            <h2 id="cookie-title" className="text-base font-semibold text-gray-900">
              Cookies & your privacy
            </h2>
            <p id="cookie-desc" className="mt-1 text-sm text-gray-600">
              We use cookies to keep things running and to improve your experience. Non-essential cookies are enabled by
              default.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 md:ml-4">
            <button
              type="button"
              className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              onClick={() => setShowDetails(true)}
            >
              Learn more
            </button>
            <button
              type="button"
              className="px-3 py-2 text-sm font-medium rounded-lg bg-gray-900 text-white hover:bg-gray-800"
              onClick={() => apply({ essential: true, analytics: true, marketing: true })}
            >
              Accept
            </button>
          </div>
        </div>
      ) : (
        <Details onBack={() => setShowDetails(false)} onSave={apply} />
      )}
    </div>
  )
}

function Details({ onBack, onSave }: { onBack: () => void; onSave: (c: ConsentRecord['categories']) => void }) {
  const [analytics, setAnalytics] = useState(true)
  const [marketing, setMarketing] = useState(true)

  const save = () => {
    onSave({ essential: true, analytics, marketing })
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-base font-semibold text-gray-900">Manage cookie preferences</h2>
        <p className="mt-1 text-sm text-gray-600">
          Essential cookies keep the site secure and functional. Analytics helps us understand usage to improve
          features. Marketing is used to personalize content and offers.
        </p>
      </div>

      <div className="space-y-3">
        <PrefRow
          label="Essential"
          description="Required for authentication, security, and core functionality."
          locked
          checked
        />
        <PrefRow
          label="Analytics"
          description="Helps us understand usage and improve features."
          checked={analytics}
          onChange={setAnalytics}
        />
        <PrefRow
          label="Marketing"
          description="Personalizes content and offers across sites."
          checked={marketing}
          onChange={setMarketing}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2 justify-end">
        <a
          href="https://www.dupr.com/privacy-policy"
          target="_blank"
          rel="noreferrer"
          className="mr-auto text-sm text-gray-500 underline underline-offset-4 hover:text-gray-700"
        >
          Privacy Policy
        </a>
        <button
          type="button"
          className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          onClick={onBack}
        >
          Back
        </button>
        <button
          type="button"
          className="px-3 py-2 text-sm font-medium rounded-lg bg-gray-900 text-white hover:bg-gray-800"
          onClick={save}
        >
          Save choices
        </button>
      </div>
    </div>
  )
}

function PrefRow({
  label,
  description,
  checked,
  onChange,
  locked,
}: {
  label: string
  description: string
  checked?: boolean
  onChange?: (v: boolean) => void
  locked?: boolean
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-gray-200 p-3">
      <div className="max-w-[75%]">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900">{label}</span>
          {locked && (
            <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
              Always on
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-gray-600">{description}</p>
      </div>

      <div className="shrink-0">
        <label className="inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="h-4 w-4 text-gray-900 border-gray-300 rounded"
            checked={!!checked}
            onChange={(e) => !locked && onChange?.(e.target.checked)}
            disabled={locked}
          />
        </label>
      </div>
    </div>
  )
}
